import React, { useState } from 'react';
import GoogleLoginButton from './GoogleLoginButton';
import FacebookLoginButton from './FacebookLoginButton';
import { loginWithGoogle, loginWithFacebook } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Social Authentication Buttons Component
 * Implements US-1.1.3A: Social Authentication (Google/Facebook OAuth)
 *
 * SCRUM-1790: Uses GoogleLoginButton (which applies SCRUM-1788's Approach B
 * fix) rather than @react-oauth/google's <GoogleLogin> directly. Prevents
 * GIS button personalization from leaking the prior Google user's name and
 * email on /guides/signup or any other page that renders this component.
 */
export default function SocialAuthButtons({ userType = 'customer', onSuccess, onError }) {
  const { login: authLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError(null);

    try {
      // Send Google credential to backend
      const response = await loginWithGoogle(credentialResponse.credential, userType);

      // Update auth context with user
      if (authLogin && response.user) {
        await authLogin(response.user);
      }

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(err.message || 'Failed to sign in with Google');

      // Call error callback if provided
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    const errorMsg = 'Google sign in was cancelled or failed';
    setError(errorMsg);
    console.error(errorMsg);

    if (onError) {
      onError(new Error(errorMsg));
    }
  };

  const handleFacebookSuccess = async ({ accessToken }) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loginWithFacebook(accessToken, userType);

      if (authLogin && response.user) {
        await authLogin(response.user);
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in with Facebook');
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookError = (err) => {
    const message = err && err.message ? err.message : 'Facebook sign in failed';
    setError(message);
    if (onError) {
      onError(err instanceof Error ? err : new Error(message));
    }
  };

  return (
    <>
      <div className="space-y-3 mb-6">
        {/* Google OAuth Button — uses SCRUM-1788's PII-safe overlay (SCRUM-1790) */}
        <div className="w-full google-oauth-full-width">
          <GoogleLoginButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isLoading}
          />
        </div>

        {/* Facebook OAuth Button (SCRUM-1793) */}
        <div className="w-full">
          <FacebookLoginButton
            onSuccess={handleFacebookSuccess}
            onError={handleFacebookError}
            disabled={isLoading}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 py-2 px-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-gray-600 text-sm text-center">
            Signing in...
          </div>
        )}
      </div>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">or</span>
        </div>
      </div>
    </>
  );
}
